import { Alignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, Image, Label, LabelProps, LabelShapeType } from "@mcleod/components";
import { AbstractComponentDemoExamples } from "./AbstractComponentDemoExamples";
import { LiveExampleComponents } from "../LiveExampleComponents";

export class DemoLabelExamples extends AbstractComponentDemoExamples {
    protected override registerExamples() {
        super.registerExamples();
        this.registerLiveDemos({
            name: "Link/Email/Phone Types",
            description: "See how labels that display links, email addresses, and phone numbers can be clicked to invoke the appropriate action.",
            layoutPath: "component-demo/components/examples/LiveDemoLinkEmailPhone"
        });
        this.registerPropertyExamples("allowSelect", {
            examples: [
                { value: true, description: "Note how you can select the text here." },
                { value: false, description: "Note how you can't select the text here." },
            ]
        });
        this.registerPropertyExamples("busy", {
            examples: [
                {
                    value: true,
                    description: "A label that is busy displays the rotating spinner icon (commonly called the busy spinner)."
                },
                {
                    value: false,
                    props: {
                        imageName: ImageName.add
                    },
                    onCreate: (exampleComponents: LiveExampleComponents) => {
                        const buttonMakeBusy = new Button({
                            caption: "Make Example Label Busy",
                            width: 200,
                            backgroundColor: "primary",
                            color: "primary.reverse",
                            marginLeft: 15,
                            rowBreak: false,
                            onClick: () => {
                                const exampleLabel = exampleComponents.liveComponent as Label;
                                exampleLabel.busy = true;
                                buttonMakeBusy.enabled = false;
                                setTimeout(() => {
                                    exampleLabel.busy = false;
                                    buttonMakeBusy.enabled = true;
                                }, 5000);
                            }
                        });
                        exampleComponents.liveComponent.rowBreak = false;
                        exampleComponents.livePanel.add(buttonMakeBusy);
                    },
                    description: "A label that becomes busy will restore its pre-busy value for the imageName property. This can be demonstrated below."
                }
            ]
        });
        this.registerPropertyExamples("busySpinnerAlignment", {
            examples: [
                { value: Alignment.LEFT, props: { busy: true }, description: "By default, the busy spinner displays to the left of the label's text." },
                { value: Alignment.RIGHT, props: { busy: true }, description: "The busy spinner can be displayed to the right of the label's text." }
            ]
        });
        this.registerPropertyExamples("displayType", {
            examples: [
                { value: "date" },
            ]
        });
        this.registerPropertyExamples("format", {
            examples: [
                { value: "long", props: { displayType: "date" }, description: "Setting the format to \"long\" with a displayType of DisplayType.DATE will cause the date to display as a full day name, month name, and year." },
            ]
        });
        this.registerPropertyExamples("imageName", {
            examples: [
                { value: "person" },
                { value: "circleCheck" },
            ]
        });
        this.registerPropertyExamples("imageTransition", {
            props: { imageName: ImageName.activateSlideout },
            examples: [
                {
                    value: Image.resolveTransitionValue(2000),
                    description: "The 'activateSlideout' image is displayed within the example label below.  Click the 'Rotate 45 Degrees' button to rotate the label's image 45 degrees (clockwise).  Because the transition property sets any 'transform' change to take 2 seconds, the change in rotation will take 2 seconds to complete.  The 'ease-in-out' property controls the style of the transition."
                }
            ],
            onCreate: (exampleComponents: LiveExampleComponents) => {
                const button = new Button({
                    caption: "Rotate 45 Degrees",
                    width: 200,
                    backgroundColor: "primary",
                    color: "primary.reverse",
                    marginLeft: 15,
                    onClick: () => {
                        const label = exampleComponents.liveComponent as Label;
                        label.imageRotation = (label.imageRotation ?? 0) + 45;
                    }
                });
                exampleComponents.liveComponent.rowBreak = false;
                exampleComponents.livePanel.add(button);
            }
        });
        this.registerPropertyExamples("link", {
            examples: [
                { value: "https://google.com" },
            ]
        });
        this.registerPropertyExamples("shape", {
            examples: [
                { value: LabelShapeType.STARBURST, props: { text: "30"} },
                { value: LabelShapeType.RIBBON, props: { text: "TEST"} },
            ]
        });
        this.registerPropertyExamples("verticalAlign", {
            props: { height: 120, borderWidth: 1, borderColor: "primary" },
            examples: [
                { value: "top", description: "verticalAlign = \"top\"" },
                { value: "center", description: "verticalAlign = \"center\"" },
                { value: "bottom", description: "verticalAlign = \"bottom\"" },
            ]
        });
        this.registerPropertyExamples("wrap", {
            examples: [
                { value: false, props: { text: "The quick brown fox jumped over the lazy dog", width: 140 } },
                { value: true, props: { text: "The quick brown fox jumped over the lazy dog", width: 140 } },
            ]
        });
    }

    protected override get propsForAllExamples(): Partial<LabelProps> {
        return {
            ...super.propsForAllExamples,
            text: "Test text"
        };
    }
}
